
import { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '../buttons/Button';
import DropDownButton from '../buttons/DropDownButton';
import SplitButton from '../buttons/SplitButton';
import SplitButtonCascade from '../buttons/SplitButtonCascade';
import Print from './Print';
import flags from "./flags";
import { buildUrl, isUserFilterActive } from '../utils/helper';
import './Toolbar.scss';
import '../Toolbar.scss';
import { useGridData } from './GridDataContext';

/**
 * Toolbar grid component
 * @module
 * @param {func} [onAdd] - Function invoked on entry add
 * @param {func} [onDelete] - Function invoked on entry delete
 * @param {func} [onCopy] - Function invoked on entry copy
 * @param {func} [onCancel] - Function invoked on entry cancel
 * @param {func} onRefresh - Function invoked on refresh
 * @param {func} onFilterClear - Function invoked on clear
 * @param {element} [children] - Children element
 * @param {number} [disabled] - Flags for disabling actions
 * @param {number} [filterLength] - Filter length
 * @param {func} onSwitchFilterable - On switch filterable function
 * @param {number} [hidden] - Flags for hiding actions
 * @param {func} [onPrint] - Function invoked on print
 * @param {func} onReset - Function invoked on reset
 * @param {func} onUpdateAll - Function invoked on update all entries
 * @param {func} onAbortChangesAll - Function invoked on abort all changes
 * @param {array} selected - Selected entries
 * @param {bool} [showToolbarButtons] - Show buttons with labels (not icons)  in toolbar
 * @param {string} [customToolbarBreadcrumb] - display custom breadcrumb in toolbar
 */

const POPUP_SETTINGS = {
  animate: false,
};
const SUBFORM_POPUP_SETTINGS = {
  animate: false,
  popupClass: 'subform-button-popup'
};

const ToolbarComponent = (props) => {
  const { customToolbarBreadcrumb, hidden, disabled, showToolbarButtons,
    onAdd, frms,
    onCopy, onCancel, onDelete,
    printItems, onPrint, tlac, onTlac,
    reducer, componentFactory, handleOpenSort,
    onSortClear, userFilterList, predefinedFilterList,
    filterLength, handleOpenFilter, handleConfirmFilter,
    onFilterClear, onSwitchFilterable, filterable,
    onReset, onRefresh,
    onUpdateAll, onAbortChangesAll, hiddenBack,
    handleChangeFrm, filter, onBack
  } = props;

  const selected = useGridData("selected");
  const children = useGridData("toolbarActionsComponent");
  const isGetPending = useGridData("isGetPending");
  const isDataChanged = useGridData("isDataChanged")

  const [editingAll, setEditingAll] = useState(false);

  const isHidden = useCallback((flag) => (!!(hidden & flag)), [hidden]);
  const isDisabled = useCallback((flag) => (!!(disabled & flag)), [disabled]);
  const isHiddenORDisabled = useCallback((flag) => (isHidden(flag) || isDisabled(flag)), [isHidden, isDisabled])


  const handleUpdateAll = useCallback(() => {
    if (onUpdateAll()) {
      setEditingAll(false)
    }
  }, [onUpdateAll]);

  const handleAbortChangesAll = useCallback(() => {
    setEditingAll(false);
    onAbortChangesAll()
  }, [onAbortChangesAll]);

  const handleFrmClick = useCallback((index) => {
    handleChangeFrm('frm', frms[index]);
  }, [frms, handleChangeFrm]);

  const getUserFilterList = useCallback(() => {
    let newUserFilterList = userFilterList && userFilterList.map(x => {
      return { text: x.name, imageUrl: buildUrl(`assets/icons/toolbar/filter-${isUserFilterActive(filter.filters, x) ? "active" : "add"}.svg`) }
    });
    if (predefinedFilterList?.length > 0) {
      newUserFilterList.unshift({
        text: "Preddefinované filtre",
        disableClick: true,
        render: SplitButtonCascade,
        direction: "left",
        onItemClick: (value) => handleConfirmFilter(predefinedFilterList[value], true),
        items: predefinedFilterList.map(x => {
          return {
            text: x.name, imageUrl: buildUrl(`assets/icons/toolbar/filter-${filter.filters.find(f => f.filterId === x.id) ? 'active' : 'add'}.svg`)
          }
        })
      })
    }
    return newUserFilterList || [];
  }, [filter.filters, handleConfirmFilter, predefinedFilterList, userFilterList])

  const splitButtonUserFilterList = useMemo(() => getUserFilterList(), [getUserFilterList]);
  const splitButttonhandleClick = useCallback((value) => value === -1 ? handleOpenFilter() : handleConfirmFilter(userFilterList[(predefinedFilterList.length > 0 ? value - 1 : value)], true), [handleOpenFilter, handleConfirmFilter, userFilterList, predefinedFilterList])

  const handleReset = useCallback((value) => onReset(value + 2), [onReset]);

  const isSelected = useMemo(() => selected && selected.length === 1, [selected]);
  const isMultiSelected = useMemo(() => selected && selected.length >= 1, [selected]);
  const isMultipleSelect = useMemo(() => selected && selected.length > 1, [selected]);
  const frmsItems = useMemo(() => frms && frms.map(x => ({ text: x.Name, icon: "window", disabled: (isMultipleSelect && !x.MultiEnable) })), [frms, isMultipleSelect])

  const isCellEdit = useMemo(() => !isHiddenORDisabled(flags.GRID_CELL_EDIT) && !isHiddenORDisabled(flags.GRID) && isHiddenORDisabled(flags.GRID_INLINE_EDIT), [isHiddenORDisabled])
  const isInlineEdit = useMemo(() => !isHiddenORDisabled(flags.GRID_INLINE_EDIT) && !isHiddenORDisabled(flags.GRID) && isHiddenORDisabled(flags.GRID_CELL_EDIT), [isHiddenORDisabled])

  const isAdd = useMemo(() => !isHidden(flags.DETAIL) || isCellEdit || isInlineEdit, [isHidden, isCellEdit, isInlineEdit])

  return <>
    {customToolbarBreadcrumb ? (<span className="custom-breadcrumb">{customToolbarBreadcrumb}</span>) : null}
    <div id="grid-toolbar" className={`toolbar ${customToolbarBreadcrumb ? "custom-breadcrumb-no-margin" : ""}`}>
      {!isHidden(flags.TOOLBAR) &&
        <>
          <span className="left">
            {!hiddenBack && (
              <Button
                onClick={onBack}
                primary={true}
                imageUrl={buildUrl("assets/icons/toolbar/spät.svg")}
                title="Späť"
                className="button-back"
              />
            )}
            {showToolbarButtons && isDataChanged && (
              <>
                <Button onClick={handleUpdateAll} label="Uložiť zmeny" title="Uložiť zmeny" className="button-changes button-save-changes" />
                <Button onClick={handleAbortChangesAll} label="Zrušiť zmeny" title="Zrušiť zmeny" className="button-changes button-cancel-changes" />
              </>
            )}
            {!isHidden(flags.ADD) && isAdd &&
              <Button
                onClick={onAdd}
                primary={true}
                disabled={isDisabled(flags.ADD | flags.LEFT_TOOLBAR | flags.TOOLBAR) || (isHiddenORDisabled(flags.SAVE) && isHiddenORDisabled(flags.SAVE_NEW) && isHiddenORDisabled(flags.SAVE_BACK) && isHiddenORDisabled(flags.GRID_CELL_EDIT) && isHiddenORDisabled(flags.GRID_INLINE_EDIT)) || isGetPending}
                title="Nový záznam"
                icon="novy-zaznam"
                imageUrl={buildUrl('assets/icons/toolbar/new.svg')}
              />
            }
            {!isHidden(flags.COPY) && isAdd &&
              <Button
                onClick={onCopy}
                disabled={!isSelected || isDisabled(flags.COPY | flags.LEFT_TOOLBAR | flags.TOOLBAR) || (isHiddenORDisabled(flags.SAVE) && isHiddenORDisabled(flags.SAVE_NEW) && isHiddenORDisabled(flags.SAVE_BACK) && isHiddenORDisabled(flags.GRID_CELL_EDIT) && isHiddenORDisabled(flags.GRID_INLINE_EDIT)) || isGetPending}
                primary={true}
                title="Nový záznam ako kópia"
                imageUrl={buildUrl('assets/icons/toolbar/copy.svg')}
              />
            }
            {!isHidden(flags.CANCEL) &&
              <Button
                onClick={onCancel}
                disabled={!isMultiSelected || isDisabled(flags.CANCEL | flags.LEFT_TOOLBAR | flags.TOOLBAR)}
                primary={true}
                icon="cancel"
                title="Zrušenie záznamu"
                imageUrl={buildUrl('assets/icons/toolbar/cancel.svg')}
              />
            }
            {!isHidden(flags.DELETE) &&
              <Button
                onClick={onDelete}
                disabled={!isMultiSelected || isDisabled(flags.DELETE | flags.LEFT_TOOLBAR | flags.TOOLBAR)}
                primary={true}
                icon="delete"
                title="Zmazanie záznamu"
                imageUrl={buildUrl('assets/icons/toolbar/delete.svg')}
              />
            }
            {!showToolbarButtons && editingAll && isCellEdit &&
              <>
                <Button
                  onClick={handleUpdateAll}
                  primary={true}
                  icon="track-changes-accept"
                  title="Uložiť zmeny"
                  disabled={isDisabled(flags.TOOLBAR)}
                />
                <Button
                  onClick={handleAbortChangesAll}
                  primary={true}
                  icon="track-changes-reject"
                  title="Zrušiť úpravu"
                  disabled={isDisabled(flags.TOOLBAR)}
                />
              </>
            }
            {frmsItems?.length !== 0 &&
              <DropDownButton
                // text=""
                title="Podradené formuláre"
                imageUrl={buildUrl('assets/icons/toolbar/podriad-formular.svg')}
                items={frmsItems}
                onClick={handleFrmClick}
                popupSettings={SUBFORM_POPUP_SETTINGS}
                disabled={!isMultiSelected}
              />
            }
            {(children?.length ?? 0) !== 0 && <span className='delimeter'></span>}
            {children}
          </span>

          <span className="right">
            {(!isHidden(flags.PRINT)) &&
              <Print
                disabled={isDisabled(flags.PRINT | flags.TOOLBAR)}
                printItems={printItems}
                onPrint={onPrint}
                tlac={tlac}
                onTlac={onTlac}
                isSelected={isSelected}
                isMultiSelected={isMultiSelected}
                reducer={reducer}
                componentFactory={componentFactory}
                selected={selected}
              />
            }
            {!isHidden(flags.SORT_DIALOG | flags.SORT) &&
              <Button
                onClick={handleOpenSort}
                primary={true}
                imageUrl={buildUrl('assets/icons/toolbar/zoradenie.svg')}
                title="Zoradenie"
                disabled={isDisabled(flags.SORT | flags.SORT_DIALOG | flags.TOOLBAR)}
              />
            }
            {!isHidden(flags.SORT) &&
              <Button
                onClick={onSortClear}
                primary={true}
                imageUrl={buildUrl('assets/icons/toolbar/zrus-zoradenie.svg')}
                title="Zrušenie zoradenia"
                disabled={isDisabled(flags.SORT | flags.TOOLBAR)}
              />
            }
            {!isHidden(flags.FILTER) &&
              <>
                {(userFilterList?.length === 0 && predefinedFilterList?.length === 0) ?
                  <Button
                    onClick={handleOpenFilter}
                    imageUrl={buildUrl(`assets/icons/toolbar/filter-${filterLength > 0 ? "active" : "add"}.svg`) }
                    title="Filter"
                    disabled={isDisabled(flags.FILTER | flags.TOOLBAR)}
                  />
                  :
                  <SplitButton
                    className="compact-split-button"
                    onClick={splitButttonhandleClick}
                    imageUrl={buildUrl(`assets/icons/toolbar/filter-${filterLength > 0 ? "active" : "add"}.svg`) }
                    title="Filter"
                    tooltip={filter?.name ? `Aktívny filter: ${filter.name}` : "Filter"}
                    text=""
                    disabled={isDisabled(flags.FILTER | flags.TOOLBAR)}
                    popupSettings={POPUP_SETTINGS}
                    items={splitButtonUserFilterList}
                    primary={true}
                  />
                }
                <Button
                  onClick={onFilterClear}
                  // icon="filter-clear"
                  title="Zrušenie filtra"
                  // imageUrl={buildUrl('assets/icons/toolbar_cg_filter.svg')}
                  imageUrl={buildUrl('assets/icons/toolbar/zrus-filter.svg')}
                  disabled={isDisabled(flags.FILTER | flags.TOOLBAR)}
                />
                <Button
                  onClick={onSwitchFilterable}
                  imageUrl={filterable ? buildUrl('assets/icons/toolbar/minimize.svg') : buildUrl('assets/icons/toolbar/expand.svg')}
                  title="Mód"
                  disabled={isDisabled(flags.FILTER | flags.TOOLBAR)}
                />
              </>
            }
            {!isHidden(flags.RESET) &&
              <SplitButton
                className="compact-split-button reset"
                onClick={handleReset}
                imageUrl={buildUrl('assets/icons/toolbar/reset.svg')}
                title="Obnoviť vzhľad a zoradenie"
                tooltip="Obnoviť vzhľad a zoradenie"
                text=""
                disabled={isDisabled(flags.RESET | flags.TOOLBAR)}
                popupSettings={POPUP_SETTINGS}
                items={[{ text: "Obnoviť vzhľad", icon: "reset-sm" }, { text: "Obnoviť zoradenie", icon: "reset-sm" }]}
                primary
              />
            }
            <Button
              onClick={onRefresh}
              title="Aktualizovať údaje"
              imageUrl={buildUrl('assets/icons/toolbar/refresh.svg')}
              disabled={isDisabled(flags.TOOLBAR)}
            />
          </span>
        </>
      }
    </div>
  </>

}

ToolbarComponent.propTypes = {
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onCopy: PropTypes.func,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onSortClear: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.element,
  ]),
  disabled: PropTypes.number,
  filterLength: PropTypes.number,
  onSwitchFilterable: PropTypes.func.isRequired,
  hidden: PropTypes.number,
  onPrint: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onUpdateAll: PropTypes.func,
  onAbortChangesAll: PropTypes.func,
  selected: PropTypes.array.isRequired,
  handleOpenSort: PropTypes.func,
  showToolbarButtons: PropTypes.bool,
  customToolbarBreadcrumb: PropTypes.string,
  isDataChanged: PropTypes.bool,
  handleConfirmFilter: PropTypes.func,
  userFilterList: PropTypes.array,
  predefinedFilterList: PropTypes.array,
  reducer: PropTypes.func,
  componentFactory: PropTypes.func,
  isGetPending: PropTypes.bool
}

export default ToolbarComponent;